// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Category } from "@insite/client-framework/Services/CategoryService";

import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { TurnerCategoryModel } from "@turnerBlueprint/App/Services/TurnerApiModels";
import { getDataLayer } from "@turnerBlueprint/XDL/Utilities/DataLayer";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps {
    categories: Category[];
    maxCategoriesPerRow: string;
    maxCategoryImageHeight: any;
    maxNumberofCategoriesPerRow: any;
    onClick: any;
}

type Props = OwnProps;

export interface CategoriesGalleryStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    desktopImageContainer?: FlexWrapContainerProps;
    mobileImageContainer?: GridContainerProps;
    mobileGridItemImageContainer?: GridItemProps;
    imageItem?: FlexItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
}

export const categoriesGalleryStyles: CategoriesGalleryStyles = {
    titleText: {
        variant: "h3",
        transform: "inherit",
        css: css`
            text-align: left;
            margin-bottom: 0;
        `,
    },
    link: {
        css: css`
            flex-wrap: wrap;
        `,
    },
    imageItem: {
        css: css`
            padding: 10px;
            justify-content: center;
            align-items: flex-end;
            border: 1px solid #f1f1f1;
        `,
    },
    desktopImageContainer: {
        css: css`
            justify-content: flex-start;
            padding-left: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    image: {
        css: css`
            img {
                max-height: 100px !important;
            }
        `,
    },
    mobileImageContainer: {
        css: css`
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                    `,
                    css`
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    mobileGridItemImageContainer: {
        width: 6,
    },
};

const styles = categoriesGalleryStyles;

const CategoryGallery: React.FC<Props> = ({
    categories,
    maxCategoriesPerRow,
    maxCategoryImageHeight,
    maxNumberofCategoriesPerRow }) => {

    const desktop = parseInt(maxCategoriesPerRow, 10);
    const tablet = Math.ceil(desktop / 2);
    const mobile = Math.ceil(tablet / 2);

    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${maxCategoryImageHeight}px`,
        },
    };

    // Xng-Change: TSC-177 Track categories click
    const trackCategoryClick = (category: Category) => {
        const dataLayer = getDataLayer();
        if (dataLayer) {
            const googleAnalyticsData = {
                event: "shop_by_category",
                text: category.shortDescription,
                destination: category.path,
            };
            dataLayer.push(googleAnalyticsData);
        }
    }

    return (
        <>
            <GridContainer {...styles.mobileImageContainer}>
                {categories.map(category => (category as TurnerCategoryModel).random && (
                    <GridItem {...styles.mobileGridItemImageContainer}>
                        <FlexWrapContainer
                            style={{ flex: "0 0 100%", height: "100%" }}
                            key={category.id}
                        >
                            <FlexItem
                                key={category.id}
                                flexColumns={1}
                                {...styles.imageItem}
                                style={{alignItems: "normal"}}
                            >
                                <Clickable
                                    href={category.path}
                                    {...styles.link}
                                    key={category.id}
                                    onClick={() => trackCategoryClick(category)}
                                >
                                    <StyledWrapper style={{ flex: "0 0 100%", textAlign: "center" }}>
                                        <LazyImage
                                            imgProps={imgProps}
                                            {...styles.image}
                                            src={
                                                category.largeImagePath
                                            }
                                            altText={
                                                category.imageAltText
                                            }
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper style={{ flex: "0 0 100%", textAlign: "center" }}>
                                        <Typography weight={700}>
                                            {category.shortDescription}
                                        </Typography>
                                    </StyledWrapper>
                                </Clickable>
                            </FlexItem>
                        </FlexWrapContainer>
                    </GridItem>
                ))}
            </GridContainer>
            <FlexWrapContainer {...styles.desktopImageContainer}>
                {categories.map(category => (category as TurnerCategoryModel).random && (
                    <FlexItem
                        flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                        key={category.id}
                        style={{ paddingRight: "15px", paddingBottom: "15px", height: "200px", width: "215px", marginRight: "5px" }}
                        className="categoryGalleryFlexItem"
                    >
                        <FlexWrapContainer
                            style={{ flex: "0 0 100%" }}
                            key={category.id}
                        >
                            <FlexItem
                                key={category.id}
                                flexColumns={1}
                                {...styles.imageItem}
                            >
                                <Clickable
                                    href={category.path}
                                    {...styles.link}
                                    key={category.id}
                                    onClick={() => trackCategoryClick(category)}
                                >
                                    <StyledWrapper style={{ flex: "0 0 100%", textAlign: "center" }}>
                                        <LazyImage
                                            imgProps={imgProps}
                                            {...styles.image}
                                            src={
                                                category.largeImagePath
                                            }
                                            altText={
                                                category.imageAltText
                                            }
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper style={{ flex: "0 0 100%", textAlign: "center" }}>
                                        <Typography weight={700}>
                                            {category.shortDescription}
                                        </Typography>
                                    </StyledWrapper>
                                </Clickable>
                            </FlexItem>
                        </FlexWrapContainer>
                    </FlexItem>
                ))}
            </FlexWrapContainer>
        </>
    );
};

export default CategoryGallery;
